import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/**
 * Helpers for the lead page's Emails tab: who an address is, how emails group
 * into conversations, and the small formatting both the list and the reading
 * panel share.
 */

const SUBJECT_PREFIX = /^\s*((re|fw|fwd|aw|sv|wg)\s*(\[\d+\])?\s*:\s*)+/i;

/** "Re: Fwd: Quote" -> "quote". Used to keep a conversation together. */
export const baseSubject = (s) => (s || "").replace(SUBJECT_PREFIX, "").trim().toLowerCase();

/** The subject as people wrote it first, without the Re:/Fwd: chain. */
export const cleanSubject = (s) => (s || "").replace(SUBJECT_PREFIX, "").trim() || "(no subject)";

/** First address in a comma list. */
export const firstAddress = (list) => (list || "").split(",")[0].trim();

/**
 * Group emails into conversations. Two emails share a conversation when one
 * answers the other (Message-ID / In-Reply-To / thread id) or, for mail sent
 * before Message-IDs were recorded, when their subjects match without the
 * Re:/Fwd: prefixes. Union-find, so a chain of replies ends up as one group.
 */
export function groupThreads(emails) {
    const parent = new Map();
    const find = (k) => {
        while (parent.get(k) !== k) {
            parent.set(k, parent.get(parent.get(k)));
            k = parent.get(k);
        }
        return k;
    };
    const union = (a, b) => {
        if (!parent.has(a)) parent.set(a, a);
        if (!parent.has(b)) parent.set(b, b);
        const ra = find(a), rb = find(b);
        if (ra !== rb) parent.set(ra, rb);
    };

    emails.forEach(em => {
        const self = `id:${em.id}`;
        union(self, self);
        if (em.thread_id) union(self, `mid:${em.thread_id}`);
        if (em.message_id) union(self, `mid:${em.message_id}`);
        if (em.in_reply_to) union(self, `mid:${em.in_reply_to}`);
        const subj = baseSubject(em.subject);
        // Very short subjects ("hi", "ok") say nothing about a conversation.
        if (subj.length > 3) union(self, `subj:${subj}`);
    });

    const groups = new Map();
    emails.forEach(em => {
        const root = find(`id:${em.id}`);
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root).push(em);
    });

    const time = (em) => (em.sent_at ? new Date(em.sent_at).getTime() : 0);
    return [...groups.values()].map(msgs => {
        msgs.sort((a, b) => time(a) - time(b));
        const latest = msgs[msgs.length - 1];
        const sent = msgs.filter(m => m.direction === "sent");
        return {
            key: `t${msgs[0].id}`,
            messages: msgs,
            first: msgs[0],
            latest,
            count: msgs.length,
            subject: cleanSubject(msgs[0].subject),
            hasAttachments: msgs.some(m => m.has_attachments),
            opens: sent.reduce((n, m) => n + (m.open_count || (m.is_opened ? 1 : 0)), 0),
            clicked: sent.some(m => m.is_clicked),
            replied: msgs.some(m => m.direction === "received") || sent.some(m => m.is_replied),
            bounced: sent.some(m => m.is_bounced),
        };
    }).sort((a, b) => time(b.latest) - time(a.latest));
}

/** Section heading for a conversation's latest activity. */
export function dateBucket(d) {
    if (!d) return "Undated";
    const day = dayjs(d).startOf("day");
    const today = dayjs().startOf("day");
    const diff = today.diff(day, "day");
    if (diff <= 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return "This week";
    if (day.year() === today.year() && day.month() === today.month()) return "Earlier this month";
    return day.format("MMMM YYYY");
}

/** "14:05" today, "Mon" this week, "12 Oct" this year, else "12 Oct 2025". */
export function shortTime(d) {
    if (!d) return "";
    const t = dayjs(d);
    const now = dayjs();
    if (t.isSame(now, "day")) return t.format("HH:mm");
    if (now.diff(t, "day") < 7) return t.format("ddd");
    if (t.year() === now.year()) return t.format("D MMM");
    return t.format("D MMM YYYY");
}

export const fullTime = (d) => (d ? dayjs(d).format("ddd, D MMM YYYY · HH:mm") : "");
export const fromNow = (d) => (d ? dayjs(d).fromNow() : "");

/**
 * Who an address is, for display. Lead contacts by their name, our side by
 * the rep who sent it, anything else by the address's local part.
 */
export function makePersonResolver(contacts = [], accounts = []) {
    const byEmail = new Map();
    contacts.forEach(c => {
        if (c?.email) byEmail.set(c.email.trim().toLowerCase(), { name: c.name || c.email, lead: true, role: c.designation || "" });
    });
    const ours = new Set(accounts.map(a => (a.email_address || "").toLowerCase()));
    return (address, em) => {
        const a = (address || "").trim().toLowerCase();
        if (byEmail.has(a)) return { ...byEmail.get(a), address: a };
        const isOurs = ours.has(a) || (em?.direction === "sent" && a === (em.from_email || "").toLowerCase());
        if (isOurs) return { name: em?.sent_by_name || "You", lead: false, ours: true, address: a };
        const local = a.split("@")[0] || a;
        const pretty = local.replace(/[._-]+/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());
        return { name: pretty || a, lead: false, address: a };
    };
}

export const initials = (name) => {
    const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

const AVATAR_TONES = [
    "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
    "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
    "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
];

/** Stable colour per person; our own side always wears the brand colour. */
export function avatarTone(person) {
    if (person?.ours) return "bg-primary/15 text-primary";
    const s = person?.address || person?.name || "";
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_TONES[h % AVATAR_TONES.length];
}
